import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Input, InputNumber, Row, Space, Switch, Table, Tabs, Tag } from 'antd';
import Column from 'antd/es/table/Column';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import ModalTextForm from '../../components/modal/ModalTextForm';
import { validateMessages } from '../../helpers/common';
import { CauHinhHanChotKPI, HanChotKPIResponse } from '../../models/setting/Setting';
import { layDSCauHinhHanChotKPI, layHanChotTrinhKPI, luuCauHinhHanChotKPI, xoaCauHinhHanChotKPI } from '../../services/setting';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';

const monthFormat = ["MM/YYYY", "MMYYYY"];

const thuTrongTuan = ["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"];

// Hiển thị kèm thứ để thấy ngay hạn chót đã được lùi qua ngày nghỉ hay chưa
const hienThiHanChot = (ngay?: Dayjs | null) => ngay
    ? `${ngay.format("DD/MM/YYYY")} (${thuTrongTuan[ngay.day()]})`
    : "";

const CauHinhHanChotKPITab = () => {
    const [data, setData] = useState<Array<CauHinhHanChotKPI>>([]);
    const [showModalChiTiet, setShowModalChiTiet] = useState<boolean>(false);
    const [idXoa, setIdXoa] = useState<number>(0);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
    const [reloadPage, setReloadPage] = useState<boolean>(true);
    const [showModalKiemTra, setShowModalKiemTra] = useState<boolean>(false);
    const [thangKiemTra, setThangKiemTra] = useState<Dayjs | null>(null);
    const [ketQuaKiemTra, setKetQuaKiemTra] = useState<HanChotKPIResponse | null>(null);
    const [isLoadingKiemTra, setIsLoadingKiemTra] = useState<boolean>(false);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const laMacDinh = Form.useWatch('laMacDinh', form);
    const idDangSua = Form.useWatch('id', form);

    // Chỉ tồn tại duy nhất một cấu hình mặc định, các bản ghi còn lại gắn với một tháng cụ thể
    const daCoMacDinh = data.some(x => x.laMacDinh && x.id !== idDangSua);

    const setFormDetailModal = (value: CauHinhHanChotKPI | null) => {
        form.setFieldsValue({
            "id": value?.id || 0,
            "laMacDinh": value ? value.laMacDinh : false,
            "thangNam": value?.thangNam || null,
            "ngayNhanVien": value?.ngayNhanVien || 2,
            "ngayLanhDaoPhong": value?.ngayLanhDaoPhong || 3,
            "ngayBanDieuHanh": value?.ngayBanDieuHanh || 5,
            "ghiChu": value?.ghiChu || "",
        });
        setShowModalChiTiet(true);
    };

    const layDuLieu = async () => {
        try {
            setIsLoadingTable(true);
            let rs = await layDSCauHinhHanChotKPI();
            setData(rs);
        } catch (err: any) {
            toast.error('Đã xảy ra lỗi, không lấy được cấu hình ngày trình KPI!');
        }
        setIsLoadingTable(false);
    };

    const onFinish = async (values: any) => {
        if (values.ngayNhanVien >= values.ngayLanhDaoPhong || values.ngayLanhDaoPhong >= values.ngayBanDieuHanh) {
            toast.error('Hạn chót phải tăng dần theo cấp: người lao động < lãnh đạo phòng < ban điều hành (trừ GĐ) (trừ GĐ)');
            return;
        }

        try {
            dispatch(showLoadingScreen());
            setIsLoadingBtn(true);
            await luuCauHinhHanChotKPI({
                id: values.id,
                // Cấu hình mặc định không gắn với tháng nào
                thangNam: values.laMacDinh ? null : dayjs(values.thangNam).startOf('month').format("YYYY-MM-DD"),
                ngayNhanVien: values.ngayNhanVien,
                ngayLanhDaoPhong: values.ngayLanhDaoPhong,
                ngayBanDieuHanh: values.ngayBanDieuHanh,
                ghiChu: values.ghiChu || "",
            });
            setShowModalChiTiet(false);
            toast.success('Lưu cấu hình ngày trình KPI thành công!');
            setReloadPage(true);
        } catch (err: any) {
            toast.error(err?.message || 'Đã xảy ra lỗi, lưu không thành công!');
        }
        dispatch(hideLoadingScreen());
        setIsLoadingBtn(false);
    };

    const moModalKiemTra = () => {
        setThangKiemTra(dayjs());
        setKetQuaKiemTra(null);
        setShowModalKiemTra(true);
    };

    // Gọi API tính hạn chót thực tế của tháng được chọn để đối chiếu với cấu hình đang khai báo
    const onKiemTra = async () => {
        if (!thangKiemTra) {
            toast.error('Vui lòng chọn tháng cần kiểm tra');
            return;
        }

        try {
            setIsLoadingKiemTra(true);
            let rs = await layHanChotTrinhKPI(dayjs(thangKiemTra).format("MM/YYYY"));
            setKetQuaKiemTra(rs);
        } catch (err: any) {
            setKetQuaKiemTra(null);
            toast.error(err?.message || 'Đã xảy ra lỗi, không kiểm tra được hạn chót trình KPI!');
        }
        setIsLoadingKiemTra(false);
    };

    const onDelete = async () => {
        try {
            setIsLoadingBtn(true);
            await xoaCauHinhHanChotKPI(idXoa);
            toast.success('Xoá cấu hình ngày trình KPI thành công!');
            setReloadPage(true);
        } catch (err: any) {
            toast.error(err?.message || 'Đã xảy ra lỗi, xoá không thành công!');
        }
        setIdXoa(0);
        setIsLoadingBtn(false);
    };

    useEffect(() => {
        if (reloadPage) {
            layDuLieu();
            setReloadPage(false);
        }
    }, [reloadPage]);

    return <section>
        {idXoa > 0 && <ModalTextForm show={idXoa > 0}
            title="Xoá cấu hình ngày trình KPI"
            handleClose={() => setIdXoa(0)}
            handleSave={onDelete}>
            Xác nhận xoá cấu hình của tháng đang chọn?
        </ModalTextForm>}
        {showModalKiemTra && <ModalTextForm title="Kiểm tra hạn chót trình KPI"
            hideFooter
            show={showModalKiemTra}
            handleClose={() => setShowModalKiemTra(false)}
        >
            <Row gutter={16} align="bottom">
                <Col span={12}>
                    <label>Kiểm tra KPI tháng</label>
                    <DatePicker
                        changeOnBlur={true}
                        style={{ width: "100%" }}
                        placeholder="Tháng cần kiểm tra"
                        format={monthFormat}
                        picker="month"
                        value={thangKiemTra}
                        onChange={(value) => {
                            setThangKiemTra(value);
                            setKetQuaKiemTra(null);
                        }}
                    />
                </Col>
                <Col span={12}>
                    <Button type="primary" loading={isLoadingKiemTra}
                        icon={<SearchOutlined rev="" />}
                        onClick={onKiemTra}
                    >
                        Kiểm tra
                    </Button>
                </Col>
            </Row>
            {ketQuaKiemTra && <div className="mt-3">
                <Row>
                    <Col span={24}>
                        <b>Hạn chót trình KPI tháng {dayjs(thangKiemTra).format("MM/YYYY")}</b>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col span={10}>Người lao động</Col>
                    <Col span={14}>{hienThiHanChot(ketQuaKiemTra.ngayNhanVien)}</Col>
                </Row>
                <Row>
                    <Col span={10}>Lãnh đạo phòng</Col>
                    <Col span={14}>{hienThiHanChot(ketQuaKiemTra.ngayLanhDaoPhong)}</Col>
                </Row>
                <Row>
                    <Col span={10}>Ban điều hành (trừ GĐ)</Col>
                    <Col span={14}>{hienThiHanChot(ketQuaKiemTra.ngayBanDieuHanh)}</Col>
                </Row>
                <Row className="mt-2">
                    <Col span={24}>
                        <span className="text-muted">
                            Đây là hạn chót thực tế hệ thống áp dụng, đã lùi qua ngày nghỉ / ngày lễ.
                        </span>
                    </Col>
                </Row>
            </div>}
        </ModalTextForm>}
        {showModalChiTiet && <ModalTextForm title="Cấu hình ngày trình KPI"
            hideFooter
            show={showModalChiTiet}
            handleClose={() => setShowModalChiTiet(false)}
        >
            <Form
                layout="vertical"
                validateMessages={validateMessages}
                onFinish={onFinish}
                form={form}
            >
                <Form.Item hidden name="id"><Input /></Form.Item>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Cấu hình mặc định"
                            name="laMacDinh"
                            valuePropName="checked"
                            tooltip="Áp dụng cho mọi tháng không có cấu hình riêng"
                        >
                            <Switch disabled={daCoMacDinh} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        {!laMacDinh && <Form.Item
                            label="Áp dụng cho KPI tháng"
                            name="thangNam"
                            rules={[{ required: true, message: 'Vui lòng chọn tháng áp dụng' }]}
                        >
                            <DatePicker
                                changeOnBlur={true}
                                style={{ width: "100%" }}
                                placeholder="Tháng áp dụng"
                                format={monthFormat}
                                picker="month"
                            />
                        </Form.Item>}
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            label="Người lao động"
                            name="ngayNhanVien"
                            rules={[{ required: true, message: 'Vui lòng nhập ngày hạn chót' }]}
                        >
                            <InputNumber style={{ width: "100%" }} min={1} max={28} precision={0} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Lãnh đạo phòng"
                            name="ngayLanhDaoPhong"
                            rules={[{ required: true, message: 'Vui lòng nhập ngày hạn chót' }]}
                        >
                            <InputNumber style={{ width: "100%" }} min={1} max={28} precision={0} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Ban điều hành (trừ GĐ)"
                            name="ngayBanDieuHanh"
                            rules={[{ required: true, message: 'Vui lòng nhập ngày hạn chót' }]}
                        >
                            <InputNumber style={{ width: "100%" }} min={1} max={28} precision={0} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <span className="text-muted">
                            Hạn chót là ngày của tháng kế tiếp kỳ KPI. Nếu rơi vào ngày nghỉ, hệ thống tự lùi sang ngày làm việc gần nhất.
                        </span>
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col span={24}>
                        <Form.Item label="Ghi chú" name="ghiChu">
                            <Input.TextArea rows={2} placeholder="Ghi chú" />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item>
                    <Button type="primary" loading={isLoadingBtn} className="float-right" htmlType="submit" icon={<SaveOutlined rev="" />}>
                        Lưu
                    </Button>
                </Form.Item>
            </Form>
        </ModalTextForm>}
        <Tabs
            defaultActiveKey="1"
            tabBarExtraContent={
                <Button
                    className="hrm-btn hrm-btn-warning"
                    onClick={() => setFormDetailModal(null)}
                    icon={<PlusOutlined rev="" />}
                >
                    Thêm mới
                </Button>
            }
            items={[
                {
                    key: '1',
                    label: 'Cấu hình ngày trình KPI',
                    children:
                        <div className="card">
                            <Table
                                loading={isLoadingTable}
                                locale={{ emptyText: "Không có dữ liệu" }}
                                dataSource={data.map((item: any, index) => { item.key = index; return item; })}
                                pagination={false}
                            >
                                <Column
                                    width="7%"
                                    title="STT"
                                    key="STT"
                                    align="center"
                                    render={(value, record, index) => (<>{index + 1}</>)}
                                />
                                <Column
                                    title="Áp dụng cho KPI tháng"
                                    key="thangNam"
                                    align="center"
                                    render={(record: CauHinhHanChotKPI) => (
                                        record.laMacDinh
                                            ? <Tag color="blue">Mặc định</Tag>
                                            : <>{dayjs(record.thangNam).format("MM/YYYY")}</>
                                    )}
                                />
                                <Column title="Hạn chót người lao động" dataIndex="ngayNhanVien" key="ngayNhanVien" align="center" />
                                <Column title="Hạn chót lãnh đạo phòng" dataIndex="ngayLanhDaoPhong" key="ngayLanhDaoPhong" align="center" />
                                <Column title="Hạn chót ban điều hành (trừ GĐ)" dataIndex="ngayBanDieuHanh" key="ngayBanDieuHanh" align="center" />
                                <Column title="Ghi chú" dataIndex="ghiChu" key="ghiChu" />
                                <Column
                                    title=""
                                    key="action"
                                    align="center"
                                    render={(record: CauHinhHanChotKPI) => (
                                        <Space>
                                            <Button type="primary" size={"small"} title="Sửa"
                                                icon={<EditOutlined rev="" />}
                                                onClick={() => setFormDetailModal(record)}
                                            >
                                                Sửa
                                            </Button>
                                            {/* Cấu hình mặc định áp dụng cho mọi tháng nên cần chọn tháng để xem hạn chót thực tế */}
                                            {record.laMacDinh &&
                                                <Button type="primary" size={"small"} title="Kiểm tra hạn chót theo tháng"
                                                    icon={<SearchOutlined rev="" />}
                                                    onClick={moModalKiemTra}
                                                >
                                                    Kiểm tra
                                                </Button>}
                                            {/* Cấu hình mặc định là mốc dự phòng cho mọi tháng nên không cho xoá */}
                                            {!record.laMacDinh &&
                                                <Button type="primary" danger size={"small"} title="Xóa"
                                                    icon={<DeleteOutlined rev="" />}
                                                    onClick={() => setIdXoa(record.id)}
                                                >
                                                    Xóa
                                                </Button>}
                                        </Space>
                                    )}
                                />
                            </Table>
                        </div>
                },
            ]}>
        </Tabs>
    </section>
}

export default CauHinhHanChotKPITab;
